import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { MessageCircle, Send, X, Loader2, Sparkles, LogIn } from "lucide-react";

const ANON_LIMIT = 3;
const ANON_COUNT_KEY = "pf_anon_chat_count";
const getAnonCount = () => parseInt(localStorage.getItem(ANON_COUNT_KEY) || "0", 10);
const incAnonCount = () => {
  const next = getAnonCount() + 1;
  localStorage.setItem(ANON_COUNT_KEY, String(next));
  return next;
};

const PLACEHOLDER = {
  en: "Ask about streams, careers, exams…",
  hi: "स्ट्रीम, करियर, परीक्षाओं के बारे में पूछें…",
  pa: "ਸਟ੍ਰੀਮ, ਕੈਰੀਅਰ, ਪ੍ਰੀਖਿਆਵਾਂ ਬਾਰੇ ਪੁੱਛੋ…",
};

const STARTER = {
  en: [
    "How do I choose a stream after Class 10?",
    "What career suits someone who loves science and art?",
    "What is NEP 2020 and how does it affect my child?",
    "What are some good career options in commerce?",
  ],
  hi: [
    "कक्षा 10 के बाद स्ट्रीम कैसे चुनें?",
    "विज्ञान और कला दोनों पसंद करने वाले के लिए कौन-सा करियर सही है?",
    "NEP 2020 क्या है और यह मेरे बच्चे को कैसे प्रभावित करता है?",
    "कॉमर्स में अच्छे करियर विकल्प कौन-से हैं?",
  ],
  pa: [
    "10ਵੀਂ ਜਮਾਤ ਤੋਂ ਬਾਅਦ ਸਟ੍ਰੀਮ ਕਿਵੇਂ ਚੁਣਾਂ?",
    "ਵਿਗਿਆਨ ਅਤੇ ਕਲਾ ਦੋਵੇਂ ਪਸੰਦ ਕਰਨ ਵਾਲੇ ਲਈ ਕਿਹੜਾ ਕੈਰੀਅਰ ਸਹੀ ਹੈ?",
    "NEP 2020 ਕੀ ਹੈ ਅਤੇ ਇਹ ਮੇਰੇ ਬੱਚੇ ਨੂੰ ਕਿਵੇਂ ਪ੍ਰਭਾਵਿਤ ਕਰਦਾ ਹੈ?",
    "ਕਾਮਰਸ ਵਿੱਚ ਵਧੀਆ ਕੈਰੀਅਰ ਵਿਕਲਪ ਕਿਹੜੇ ਹਨ?",
  ],
};

export default function CareerCounselorChat() {
  const { user } = useAuth();
  const { lang } = useLang();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [gated, setGated] = useState(() => !user && getAnonCount() >= ANON_LIMIT);
  const bottomRef = useRef(null);

  useEffect(() => {
    if (user) setGated(false);
  }, [user]);

  useEffect(() => {
    if (open) bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open, gated]);

  const ask = async (q) => {
    const question = (q || input).trim();
    if (!question || sending) return;
    if (!user && getAnonCount() >= ANON_LIMIT) {
      setGated(true);
      return;
    }
    setInput("");
    const nextMsgs = [...messages, { role: "user", content: question }];
    setMessages(nextMsgs);
    setSending(true);
    try {
      const { data } = await api.post("/career-chat", {
        question,
        history: messages,
        language: lang,
      });
      setMessages([...nextMsgs, { role: "assistant", content: data.reply }]);
      if (!user && incAnonCount() >= ANON_LIMIT) setGated(true);
    } catch (e) {
      const msg = e.response?.status === 429
        ? "You've sent quite a few messages — please wait a bit before trying again."
        : "Sorry, I couldn't answer that right now. Please try again.";
      setMessages([...nextMsgs, { role: "assistant", content: msg }]);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {!open && (
        <button
          data-testid="career-counselor-open-btn"
          onClick={() => setOpen(true)}
          className="print:hidden fixed bottom-6 right-6 z-40 btn-brutal bg-blue-600 text-white pl-4 pr-5 py-3 flex items-center gap-2"
        >
          <MessageCircle size={18} strokeWidth={2.5} /> Ask AI Career Counselor
        </button>
      )}

      {open && (
        <div className="print:hidden fixed bottom-0 right-0 md:bottom-6 md:right-6 z-40 w-full md:w-[420px] max-h-[80vh] card-brutal bg-white flex flex-col rounded-t-2xl md:rounded-2xl overflow-hidden">
          <div className="flex items-center justify-between p-4 border-b-2 border-[#0A0A0A] bg-[#FEF08A]">
            <div className="flex items-center gap-2">
              <Sparkles strokeWidth={2.5} size={18} />
              <div>
                <div className="font-display font-extrabold text-sm">AI Career Counselor</div>
                <div className="text-[11px] text-[#52525B]">Ask about streams, careers & exams</div>
              </div>
            </div>
            <button data-testid="career-counselor-close-btn" onClick={() => setOpen(false)} className="p-2 border-2 border-[#0A0A0A] rounded-lg bg-white">
              <X size={14} strokeWidth={2.5} />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#FAFAF9]">
            {messages.length === 0 && (
              <div>
                <p className="text-sm text-[#52525B] mb-3">Try one of these:</p>
                <div className="space-y-2">
                  {(STARTER[lang] || STARTER.en).map((s, i) => (
                    <button
                      key={i}
                      data-testid={`career-counselor-starter-${i}`}
                      onClick={() => ask(s)}
                      className="w-full text-left text-sm px-3 py-2 bg-white border-2 border-[#0A0A0A] rounded-xl hover:bg-[#FEF08A]"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((m, i) => (
              <div key={i} data-testid={`career-counselor-msg-${i}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] px-3 py-2 rounded-xl border-2 border-[#0A0A0A] text-sm whitespace-pre-wrap ${
                  m.role === "user" ? "bg-blue-600 text-white" : "bg-white"
                }`}>
                  {m.content}
                </div>
              </div>
            ))}

            {sending && (
              <div className="flex justify-start">
                <div className="max-w-[80%] px-3 py-2 rounded-xl border-2 border-[#0A0A0A] bg-white text-sm flex items-center gap-2">
                  <Loader2 className="animate-spin" size={14} /> thinking…
                </div>
              </div>
            )}

            {gated && (
              <div data-testid="career-counselor-gate" className="border-2 border-[#0A0A0A] rounded-xl p-4 bg-[#FEF08A]">
                <p className="text-sm font-bold">Sign in to keep chatting</p>
                <p className="text-xs text-[#52525B] mt-1">
                  You've used your {ANON_LIMIT} free questions. Create a free account to keep exploring career options with your AI counselor.
                </p>
                <div className="mt-3 flex gap-2">
                  <Link to="/register" className="btn-brutal bg-blue-600 text-white flex-1 text-center py-2 text-xs">Sign up free</Link>
                  <Link to="/login" className="btn-brutal bg-white flex-1 text-center py-2 text-xs inline-flex items-center justify-center gap-1">
                    <LogIn size={12} strokeWidth={2.5} /> Log in
                  </Link>
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {!gated && (
            <form
              onSubmit={(e) => { e.preventDefault(); ask(); }}
              className="p-3 border-t-2 border-[#0A0A0A] bg-white flex items-center gap-2"
            >
              <input
                data-testid="career-counselor-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={PLACEHOLDER[lang] || PLACEHOLDER.en}
                className="flex-1 px-3 py-2 border-2 border-[#0A0A0A] rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                type="submit"
                data-testid="career-counselor-send-btn"
                disabled={sending || !input.trim()}
                className="btn-brutal bg-blue-600 text-white p-2 disabled:opacity-60"
                aria-label="Send"
              >
                <Send size={16} strokeWidth={2.5} />
              </button>
            </form>
          )}
        </div>
      )}
    </>
  );
}
